import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import { clearAllStorage, setAuthNotice } from './utils/authStorage';
import { unregister as unregisterServiceWorker } from './serviceWorkerRegistration';
import { APP_PREFIX } from './config/appIdentifiers';

const originalFetch = window.fetch.bind(window);
let apiReachable = true;
const NETWORK_STATUS_EVENT = `${APP_PREFIX}:network-status`;

const getRequestUrl = (input: RequestInfo | URL): string => {
  if (typeof input === 'string') {
    return input;
  }

  if (input instanceof URL) {
    return input.toString();
  }

  if (input && 'url' in input) {
    return input.url;
  }

  return '';
};

const isApiRequest = (input: RequestInfo | URL): boolean => {
  const rawUrl = getRequestUrl(input);
  if (!rawUrl) {
    return false;
  }

  try {
    const parsed = new URL(rawUrl, window.location.origin);
    return parsed.pathname.startsWith('/api/');
  } catch {
    return false;
  }
};

const dispatchNetworkStatus = (online: boolean, reason: string) => {
  window.dispatchEvent(new CustomEvent(NETWORK_STATUS_EVENT, {
    detail: {
      online,
      reason,
      source: 'fetch',
    }
  }));
};

const extractAuthHeader = (init?: RequestInit) => {
  if (!init || !init.headers) return '';
  if (init.headers instanceof Headers) {
    return init.headers.get('Authorization') || init.headers.get('authorization') || '';
  }
  if (Array.isArray(init.headers)) {
    const found = init.headers.find(([key]) => key.toLowerCase() === 'authorization');
    return found ? found[1] : '';
  }
  if (typeof init.headers === 'object') {
    return init.headers.Authorization || init.headers.authorization || '';
  }
  return '';
};

// Les garde-fous de zoom globaux ont été retirés pour éviter tout blocage
// involontaire du scroll/touch selon les navigateurs.

window.fetch = async (...args: Parameters<typeof fetch>) => {
  const apiRequest = isApiRequest(args[0]);
  let response: Response;

  try {
    response = await originalFetch(...args);
  } catch (error) {
    const isAbortError = (error as { name?: string } | null)?.name === 'AbortError';
    const browserOnline = typeof navigator === 'undefined' ? true : navigator.onLine;

    if (apiRequest && !isAbortError) {
      // Do not mark the app offline when the browser still reports connectivity.
      // This avoids false offline banners on transient API/CORS failures.
      if (!browserOnline && apiReachable) {
        apiReachable = false;
        dispatchNetworkStatus(false, 'browser_offline');
      }
    }
    throw error;
  }

  if (apiRequest && !apiReachable) {
    apiReachable = true;
    dispatchNetworkStatus(true, 'api_recovered');
  }

  const authHeader = extractAuthHeader(args[1]);

  if (response.status === 401 || response.status === 403) {
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      const data = await response.clone().json().catch(() => null);
      const isAccountStatus = data?.code === 'account_disabled' || data?.code === 'account_deletion_scheduled';
      const shouldLogout = isAccountStatus || (response.status === 401 && authHeader);

      if (shouldLogout) {
        clearAllStorage();
        setAuthNotice(data?.message || 'Votre session n\'est plus valide. Merci de vous reconnecter.');
        if (!['/login', '/register', '/'].includes(window.location.pathname)) {
          window.location.assign('/login');
        }
      }
    }
  }

  return response;
};

const container = document.getElementById('root');
if (container) {
  const root = ReactDOM.createRoot(container);
  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );
}

unregisterServiceWorker();
